import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useAuth } from "../auth";
import { api, ApiError, type TenantUser } from "../api";

/** Tenant user management — owner invites/manages helpers.
 *  - Owner: full CRUD
 *  - Manager: read-only list
 *  - Viewer: menu hidden at Layout level, so shouldn't normally land here
 */
export function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<TenantUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<TenantUser | null>(null);

  const canWrite = me?.role === "owner";

  async function refresh() {
    try {
      setUsers(await api.get<TenantUser[]>("/users"));
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function onDelete(u: TenantUser) {
    if (!window.confirm(`Xoá user ${u.email}? Thao tác không hoàn tác được.`)) return;
    try {
      await api.del<void>(`/users/${u.id}`);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 24 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Users <small>{users?.length ?? 0} người trong tenant</small>
        </h1>
        <div className="spacer" />
        {canWrite && (
          <button className="primary" onClick={() => setShowCreate(true)}>
            + Thêm user
          </button>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}
      {!canWrite && (
        <div className="alert warn">
          Bạn đang xem ở chế độ read-only. Chỉ owner mới có thể thêm/sửa user.
        </div>
      )}

      {users === null && <div className="muted">Đang tải…</div>}

      {users && (
        <div className="card table-scroll" style={{ padding: 0, overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Trạng thái</th>
                <th>Tạo lúc</th>
                {canWrite && <th style={{ textAlign: "right" }}>Thao tác</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <strong>{u.email}</strong>
                    {u.id === me?.id && <span className="muted"> (bạn)</span>}
                  </td>
                  <td>
                    <span className={`badge ${u.role === "owner" ? "admin" : "owner"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td>
                    {u.disabled_at ? (
                      <span className="badge disabled">disabled</span>
                    ) : (
                      <span className="badge owner">active</span>
                    )}
                  </td>
                  <td className="muted">{new Date(u.created_at).toLocaleDateString()}</td>
                  {canWrite && (
                    <td style={{ textAlign: "right" }}>
                      {u.id !== me?.id ? (
                        <div style={{ display: "inline-flex", gap: 6 }}>
                          <button
                            onClick={() => setEditTarget(u)}
                            style={{ fontSize: "0.8rem", padding: "4px 10px" }}
                          >
                            Sửa
                          </button>
                          <button
                            className="danger"
                            onClick={() => onDelete(u)}
                            style={{ fontSize: "0.8rem", padding: "4px 10px" }}
                          >
                            Xoá
                          </button>
                        </div>
                      ) : (
                        <span className="muted" style={{ fontSize: "0.8rem" }}>
                          Sửa profile ở trang riêng
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && canWrite && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}

      {editTarget && canWrite && (
        <EditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={() => { setEditTarget(null); refresh(); }}
        />
      )}
    </>
  );
}

function CreateUserModal({
  onClose, onCreated,
}: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"owner" | "manager" | "viewer">("manager");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post<TenantUser>("/users", { email, password, role });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Tạo user thất bại");
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Thêm user vào tenant</h2>
        <p className="muted" style={{ fontSize: "0.85rem", marginTop: 0 }}>
          Gửi email + password cho người dùng qua kênh riêng (Zalo, SMS). Họ sẽ
          login và đổi password sau.
        </p>
        <form onSubmit={onSubmit}>
          {error && <div className="alert error">{error}</div>}

          <div className="field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="field">
            <label>Mật khẩu khởi tạo (min 8 ký tự)</label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              style={{ fontFamily: "monospace" }}
            />
          </div>

          <div className="field">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value as "owner" | "manager" | "viewer")}>
              <option value="viewer">Viewer — chỉ xem dashboard</option>
              <option value="manager">Manager — vận hành (start/stop zone)</option>
              <option value="owner">Owner — full quyền, quản lý user + master</option>
            </select>
          </div>

          <div className="btn-row">
            <button type="button" onClick={onClose}>Huỷ</button>
            <button type="submit" className="primary" disabled={submitting}>
              {submitting ? "Đang tạo…" : "Tạo user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({
  user, onClose, onUpdated,
}: { user: TenantUser; onClose: () => void; onUpdated: () => void }) {
  const [role, setRole] = useState(user.role);
  const [disabled, setDisabled] = useState(user.disabled_at !== null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const hasChange = useMemo(() => (
    role !== user.role ||
    disabled !== (user.disabled_at !== null) ||
    password.length > 0
  ), [role, disabled, password, user]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {};
      if (role !== user.role) body.role = role;
      if (disabled !== (user.disabled_at !== null)) body.disabled = disabled;
      if (password) body.password = password;
      await api.put<TenantUser>(`/users/${user.id}`, body);
      onUpdated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Cập nhật thất bại");
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Sửa user</h2>
        <p className="muted" style={{ marginTop: 0 }}>{user.email}</p>
        <form onSubmit={onSubmit}>
          {error && <div className="alert error">{error}</div>}

          <div className="field">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value as "owner" | "manager" | "viewer")}>
              <option value="viewer">Viewer</option>
              <option value="manager">Manager</option>
              <option value="owner">Owner</option>
            </select>
          </div>

          <div className="field">
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={disabled}
                onChange={(e) => setDisabled(e.target.checked)}
                style={{ width: "auto" }}
              />
              <span>Disable — user không đăng nhập được</span>
            </label>
          </div>

          <div className="field">
            <label>Reset mật khẩu (để trống nếu không đổi)</label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="min 8 ký tự"
              minLength={password ? 8 : undefined}
              style={{ fontFamily: "monospace" }}
            />
          </div>

          <div className="btn-row">
            <button type="button" onClick={onClose}>Huỷ</button>
            <button type="submit" className="primary" disabled={submitting || !hasChange}>
              {submitting ? "Đang lưu…" : "Lưu"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
